import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * ActiveMeetingContext — keeps a single Jitsi iframe alive across React Router
 * route changes so members don't lose their meeting when they tap Library,
 * Calendar, AI Notes, etc. The iframe is rendered ONCE in <PlatformLayout> via
 * <ActiveMeetingHost />, and visually positions itself based on the current
 * route:
 *   • on `/platform/meetings/room/:roomName` → fullscreen inside main content
 *   • elsewhere within the platform           → floating ~280×180 PiP card
 *
 * MeetingRoom (route component) becomes a thin shell that:
 *   1. announces the active meeting (room/code/title) via `setActive(...)`
 *   2. renders a dedicated <div ref={anchorRef}> placeholder
 *   3. on mount, the host re-positions the (already-running) iframe over that
 *      placeholder. On unmount, the iframe minimises to a PiP.
 *
 * Hangup ("readyToClose" from Jitsi) is the only event that fully tears the
 * iframe down.
 */

const ActiveMeetingCtx = createContext(null);

export function useActiveMeeting() {
  const ctx = useContext(ActiveMeetingCtx);
  if (!ctx) throw new Error("useActiveMeeting must be used inside <ActiveMeetingProvider>");
  return ctx;
}

const FALLBACK_DOMAIN = "meet.jit.si";

export function ActiveMeetingProvider({ children, user }) {
  // {roomName, code, title, displayName, email}
  const [active, setActive] = useState(null);
  // anchor div the iframe should be parented to (when MeetingRoom is mounted)
  const [anchor, setAnchor] = useState(null);
  // jitsi runtime
  const apiRef = useRef(null);
  const iframeWrapRef = useRef(null);   // <div> that physically holds the JaaS iframe
  const launchLockRef = useRef(null);   // Promise of in-flight launch, prevents concurrent inits
  const [audioShared, setAudioShared] = useState(false);
  const [joined, setJoined] = useState(false);

  // Create the host wrapper once
  useEffect(() => {
    if (!iframeWrapRef.current) {
      const div = document.createElement("div");
      div.style.width = "100%";
      div.style.height = "100%";
      div.setAttribute("data-testid", "active-meeting-iframe-wrap");
      iframeWrapRef.current = div;
    }
  }, []);

  /** Boot a Jitsi/JaaS connection for the given meeting. */
  const launch = useCallback(async (meeting) => {
    if (!meeting?.roomName) return;
    // Wait for any in-flight launch to settle before deciding what to do.
    // Without this, React Strict Mode double-mount + sessionStorage hydration
    // can fire 2-3 launches in parallel, leaving orphaned iframes behind.
    if (launchLockRef.current) {
      try { await launchLockRef.current; } catch (e) { /* ignore */ }
    }
    // If we already have a working JWT-authenticated session for this same
    // room, just re-anchor (no re-launch). The `_authed` flag prevents the
    // bug where an early no-code launch on meet.jit.si would block the later
    // re-launch on 8x8.vc with proper moderator JWT.
    if (
      active &&
      active.roomName === meeting.roomName &&
      apiRef.current &&
      active._authed === !!meeting.code
    ) {
      setActive(meeting);
      return;
    }

    const launchPromise = (async () => {
    // Different meeting OR upgrading from anonymous → JaaS-authenticated.
    if (apiRef.current) {
      try { apiRef.current.dispose(); } catch (e) { /* ignore */ }
      apiRef.current = null;
    }
    // Belt-and-braces: Jitsi's SDK occasionally leaves the <iframe> behind
    // after dispose. Clear the wrapper so we don't accumulate stale frames
    // when upgrading from anonymous meet.jit.si to JaaS 8x8.vc.
    if (iframeWrapRef.current) {
      while (iframeWrapRef.current.firstChild) {
        iframeWrapRef.current.removeChild(iframeWrapRef.current.firstChild);
      }
    }
    setJoined(false);
    setAudioShared(false);
    setActive({ ...meeting, _authed: !!meeting.code });

    // Resolve JaaS token (falls back to public meet.jit.si if unavailable)
    let domain = FALLBACK_DOMAIN;
    let actualRoom = meeting.roomName;
    let jwt;
    try {
      if (meeting.code) {
        const r = await api.post("/platform/jaas/token", { code: meeting.code });
        if (r.data?.token) {
          domain = r.data.domain || "8x8.vc";
          actualRoom = r.data.room_name_full || meeting.roomName;
          jwt = r.data.token;
        }
      }
    } catch (e) { /* JaaS not configured, silent fallback */ }

    // Load external_api.js if not already
    await new Promise((resolve) => {
      if (window.JitsiMeetExternalAPI) return resolve();
      const s = document.createElement("script");
      s.src = `https://${domain}/external_api.js`;
      s.async = true;
      s.onload = resolve;
      s.onerror = resolve;
      document.body.appendChild(s);
    });
    if (!window.JitsiMeetExternalAPI) return;

    try {
      apiRef.current = new window.JitsiMeetExternalAPI(domain, {
        roomName: actualRoom,
        jwt,
        parentNode: iframeWrapRef.current,
        width: "100%",
        height: "100%",
        userInfo: {
          displayName: meeting.displayName || user?.name || "Sangha guest",
          email: meeting.email || user?.email || undefined,
        },
        configOverwrite: {
          prejoinPageEnabled: false,
          enableLobbyChat: false,
          requireDisplayName: false,
          startWithAudioMuted: true,
          startWithVideoMuted: false,
          disableDeepLinking: true,
          disableInviteFunctions: true,
          // Override the displayed room title so the raw JaaS room ID
          // (vpaas-magic-cookie-…/Room) never leaks to participants.
          subject: meeting.title || "ABGN-UK Sangha Meeting",
          // Also hide the auto-generated subject hint
          hideConferenceSubject: false,
          hideConferenceTimer: false,
        },
        interfaceConfigOverwrite: {
          DEFAULT_BACKGROUND: "#0B1F4D",
          DEFAULT_REMOTE_DISPLAY_NAME: "Sangha member",
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          SHOW_POWERED_BY: false,
          HIDE_INVITE_MORE_HEADER: true,
        },
      });
      apiRef.current.addListener("videoConferenceJoined", () => {
        setJoined(true);
        // Belt & braces: re-issue subject command after join so JaaS doesn't
        // ever fall back to showing the raw room ID in the top-left corner.
        try {
          apiRef.current?.executeCommand("subject", meeting.title || "ABGN-UK Sangha Meeting");
        } catch (e) { /* ignore */ }
      });
      apiRef.current.addListener("audioOrVideoSharingChanged", (e) => {
        // Jitsi fires this when share-audio toggles
        if (typeof e?.audio === "boolean") setAudioShared(e.audio);
      });
      apiRef.current.addListener("readyToClose", () => {
        try { apiRef.current?.dispose(); } catch (err) { /* ignore */ }
        apiRef.current = null;
        setActive(null);
        setJoined(false);
        setAudioShared(false);
      });
    } catch (e) { /* ignore */ }
    })();
    launchLockRef.current = launchPromise;
    try { await launchPromise; } finally {
      if (launchLockRef.current === launchPromise) launchLockRef.current = null;
    }
  }, [active, user]);

  /** Manual hangup — used by the PiP "X" button. */
  const hangup = useCallback(() => {
    try { apiRef.current?.executeCommand("hangup"); } catch (e) { /* ignore */ }
    try { apiRef.current?.dispose(); } catch (e) { /* ignore */ }
    apiRef.current = null;
    setActive(null);
    setJoined(false);
    setAudioShared(false);
  }, []);

  /** Toggle Jitsi's "Share audio" — broadcasts the host's tab/system audio
   *  (e.g. the ambient music track) to all participants in real time. */
  const toggleShareAudio = useCallback(() => {
    try { apiRef.current?.executeCommand("toggleShareAudio"); } catch (e) { /* ignore */ }
  }, []);

  const toggleAudio = useCallback(() => {
    try { apiRef.current?.executeCommand("toggleAudio"); } catch (e) { /* ignore */ }
  }, []);
  const toggleVideo = useCallback(() => {
    try { apiRef.current?.executeCommand("toggleVideo"); } catch (e) { /* ignore */ }
  }, []);

  const value = {
    active,
    joined,
    audioShared,
    launch,
    hangup,
    toggleShareAudio,
    toggleAudio,
    toggleVideo,
    setAnchor,
    iframeWrapRef,
    apiRef,
  };

  return (
    <ActiveMeetingCtx.Provider value={value}>
      {children}
      <ActiveMeetingHost anchor={anchor} active={active} hangup={hangup} iframeWrapRef={iframeWrapRef} />
    </ActiveMeetingCtx.Provider>
  );
}

/**
 * The element that physically owns the Jitsi iframe DOM node. When a
 * MeetingRoom anchor is registered, the iframe is moved into it via
 * appendChild (the iframe's contentWindow keeps running across moves so the
 * call is preserved). When no anchor exists, the wrapper renders as a fixed
 * floating PiP in the bottom-right.
 */
function ActiveMeetingHost({ anchor, active, hangup, iframeWrapRef }) {
  const pipRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const wrap = iframeWrapRef.current;
    if (!wrap) return;
    if (anchor) {
      if (wrap.parentNode !== anchor) anchor.appendChild(wrap);
    } else if (pipRef.current) {
      if (wrap.parentNode !== pipRef.current) pipRef.current.appendChild(wrap);
    }
  }, [anchor, active, iframeWrapRef]);

  // Render the PiP only when there's an active meeting AND no anchor
  if (!active || anchor) {
    // Keep the wrapper alive but offscreen if no meeting (so a future launch
    // can use it). When `active` exists and an anchor is set, the wrapper has
    // already been moved into the anchor by the effect above.
    return null;
  }

  return (
    <div
      data-testid="active-meeting-pip"
      className="fixed bottom-4 right-4 z-50 w-[280px] h-[180px] rounded-xl overflow-hidden shadow-2xl ring-2 ring-saffron/40 bg-[#0B1F4D] flex flex-col"
    >
      <div className="flex items-center justify-between px-2 py-1 bg-navy/90 text-pearl text-[10px]">
        <button
          onClick={() => navigate(`/platform/meetings/room/${encodeURIComponent(active.roomName)}`)}
          className="truncate text-left hover:text-saffron flex-1"
          data-testid="active-meeting-pip-expand"
          title="Return to meeting"
        >
          ⮌ {active.title || "Meeting"}
        </button>
        <button
          onClick={hangup}
          className="ml-2 px-1.5 py-0.5 bg-red-600/90 hover:bg-red-600 text-white rounded text-[10px]"
          data-testid="active-meeting-pip-hangup"
          title="Leave meeting"
        >
          End
        </button>
      </div>
      <div ref={pipRef} className="flex-1 min-h-0" />
    </div>
  );
}
